<script setup lang="ts">
import type MDBVegcoBucket from '@/types/vegco/bucket'
import { notifyError, roundTo, to_wd, to_dd_MM, to_wd_dd_MMMM_hh_mm, computeSelectionSum, emptyMongoId } from '@/utils/helpers'
import { unitésParBoîte, getDateCoupéForCulture, culturesGérées } from '@/utils/helpers-vegco'

definePage({
  meta: {
    id: 'vgco.rapportproducteurs',
    title: 'Rapport producteurs',
    icon: 'mdi-account-cowboy-hat',
    drawerIndex: 3,
    requiresAuth: true,
    requiresSiteAccess: 'vegco'
  },
})

const storeU = useUtilisateurStore()
const storeVegco = useVegcoStore()
const isLoading = ref(false)
const newData = ref(false)

const selectedSum = ref<number | null>(null)
const weekSelector = useTemplateRef('weekSelector')
const selectedWeek = ref(new Date(storeU.journéeSélectionnée))
const numberOfDays = ref(21)
const numberOfWeeks = ref(3)
const days = ref([] as (Date | string | undefined)[])
const cultures = ref([] as string[])
const crunches = [] as any[]
const tableRows = [
  { label: 'Delfland', field: 'DLFL' },
  { label: 'Hotte & Van Winden', field: 'FHVW' },
  { label: 'JPL Guerin', field: 'JPLG' },
  { label: 'Prod. Hort. Van Winden', field: 'PHVW' },
  { label: 'Prod. de la journée', field: 'totalJournée', thClass: 'small-borders-bottom' },
  { label: 'Commandes', field: 'commandes' },
  { label: '(Commandes non att.)', field: 'commandesNon' },
  { label: '(Cmds. Inv.)', field: 'commandesInv' },
  { label: '(Inventaires)', field: 'inventaires' },
  { label: 'Total', field: 'total', thClass: 'big-borders-bottom' },
]
const tableRowsJPL = [
  { label: 'JPL Guerin', field: 'JPLG' },
  { label: 'Commandes', field: 'commandes' },
  { label: '(Commandes non att.)', field: 'commandesNon' },
  { label: '(Cmds. Inv.)', field: 'commandesInv' },
  { label: '(Inventaires)', field: 'inventaires' },
  { label: 'Total', field: 'total', thClass: 'big-borders-bottom' }
]
const tableRowsHotte = [
  { label: 'Hotte & Van Winden', field: 'FHVW' },
  { label: 'Commandes', field: 'commandes' },
  { label: '(Commandes non att.)', field: 'commandesNon' },
  { label: '(Cmds. Inv.)', field: 'commandesInv' },
  { label: '(Inventaires)', field: 'inventaires' },
  { label: 'Total', field: 'total', thClass: 'big-borders-bottom' }
]

watch(selectedWeek, async (_) => {
  await nextTick()
  getData()
})

async function onWeekNumberChanged() {
  numberOfDays.value = numberOfWeeks.value * 7
  await nextTick()
  getData()
}

async function getData() {
  try{
    if (!weekSelector.value) {
      notifyError('Erreur: le sélecteur de semaine est introuvable.')
      return
    }

    isLoading.value = true
    crunches.length = 0

    const weekStart = weekSelector.value.getWeekStart()
    const weekEnd = weekSelector.value.getWeekEnd()

    const date = new Date(weekStart)
    days.value = []
    for (let i = 0; i < numberOfDays.value; i++) {
      if (i > 0 && i % 7 == 0) {
        days.value.push('Total')
        days.value.push(undefined)
      }
      days.value.push(new Date(date.getTime()))
      date.setDate(date.getDate() + 1)
    }
    days.value.push('Total')

    cultures.value = []
    for (const culture of culturesGérées()) {
      if (storeU.cultures.find(c => c.nom == culture)) {
        cultures.value.push(culture)
      }
    }

    await storeVegco.loadDaysMultipleCultures(weekStart, weekEnd, cultures.value)

    const siteId_VGCO = storeU.getSiteId('VGCO')
    const siteId_DLFL = storeU.getSiteId('DLFL')
    const siteId_FHVW = storeU.getSiteId('FHVW')
    const siteId_PHVW = storeU.getSiteId('PHVW')
    const siteId_JPLG = storeU.getSiteId('JPLG')

    for (const culture of cultures.value) {
      const crunch = {
        culture,
        days: [] as any[],
        weekTotals: [] as any[]
      }

      let i = 0
      for (const day of days.value) {
        if (!(day instanceof Date)) {
          continue
        }

        const weekNumber = Math.floor(i++ / 7)
        if (crunch.weekTotals[weekNumber] == undefined) {
          crunch.weekTotals[weekNumber] = { total: 0, totalJournée: 0, inventaires: 0,
            commandes: 0, commandesNon: 0, commandesInv: 0, DLFL: 0, FHVW: 0, JPLG: 0, PHVW: 0 }
        }
        const weekTotals = crunch.weekTotals[weekNumber]

        const entry = { date: day, total: 0, totalJournée: 0, inventaires: 0, commandes: 0, commandesNon: 0, commandesInv: 0,
          DLFL: 0, DLFL_Commandes: 0, DLFL_Buckets: [] as MDBVegcoBucket[],
          FHVW: 0, FHVW_Commandes: 0, FHVW_Buckets: [] as MDBVegcoBucket[],
          JPLG: 0, JPLG_Commandes: 0, JPLG_Buckets: [] as MDBVegcoBucket[],
          PHVW: 0, PHVW_Commandes: 0, PHVW_Buckets: [] as MDBVegcoBucket[] }

        for (const commande of storeVegco.commandesStore) {
          if (commande.statut == 'Annulé' || commande.statut == 'Estimé') {
            continue // Skip cancelled commandes
          }

          const commandeDate = getDateCoupéForCulture(commande, culture)
          if (commandeDate.getFullYear() != day.getFullYear() ||
            commandeDate.getMonth() != day.getMonth() || commandeDate.getDate() != day.getDate()) {
            continue
          }

          for (const détail of commande.détails) {
            if (détail.culture != culture) continue

            let qté = détail.quantité

            for (const bucket of storeVegco.bucketsStore) {
              if (bucket.culture == culture && bucket.commandeVenteId == commande.id &&
                bucket.codeProduit == détail.codeProduit) {
                qté -= bucket.quantité

                if (bucket.siteId == siteId_VGCO) {
                  const qté2 = bucket.quantité / unitésParBoîte(détail.culture)
                  entry.commandesInv += qté2
                  weekTotals.commandesInv += qté2
                }
              }
            }

            const realQuantité = qté / unitésParBoîte(détail.culture)
            entry.commandes += realQuantité
            entry.commandesNon += realQuantité
            weekTotals.commandes += realQuantité
            weekTotals.commandesNon += realQuantité
            entry.total -= realQuantité
            weekTotals.total -= realQuantité
          }
        }

        for (const bucket of storeVegco.bucketsStore) {
          if (bucket.culture != culture)
            continue

          const bucketDate = new Date(bucket.date)
          if (bucketDate.getFullYear() != day.getFullYear() ||
            bucketDate.getMonth() != day.getMonth() || bucketDate.getDate() != day.getDate()) {
            continue
          }

          let realQuantité = bucket.quantité
          let qtéCommandes = bucket.commandeVenteId != emptyMongoId() ? bucket.quantité : 0
          if (bucket.semiFermé && bucket.commandeVenteId == emptyMongoId()) {
            realQuantité = 0
          }

          realQuantité = realQuantité / unitésParBoîte(bucket.culture)
          qtéCommandes = qtéCommandes / unitésParBoîte(bucket.culture)

          if (bucket.semiId === emptyMongoId() && bucket.commandeVenteId == emptyMongoId()) {
            entry.inventaires += realQuantité
            weekTotals.inventaires += realQuantité
          } else {

            if (bucket.siteId != siteId_VGCO || bucket.commandeVenteId == emptyMongoId()) {
              if (qtéCommandes > 0) {
                entry.commandes += qtéCommandes
                weekTotals.commandes += qtéCommandes
              } else {
                entry.total += realQuantité
                weekTotals.total += realQuantité
              }
            }

            if (bucket.siteId != siteId_VGCO) {
              entry.totalJournée += realQuantité
              weekTotals.totalJournée += realQuantité
            }

            switch (bucket.siteId) {
              case siteId_DLFL:
                entry.DLFL += realQuantité
                entry.DLFL_Commandes += qtéCommandes
                entry.DLFL_Buckets.push(bucket)
                weekTotals.DLFL += realQuantité
                break
              case siteId_FHVW:
                entry.FHVW += realQuantité
                entry.FHVW_Commandes += qtéCommandes
                entry.FHVW_Buckets.push(bucket)
                weekTotals.FHVW += realQuantité
                break
              case siteId_PHVW:
                entry.PHVW += realQuantité
                entry.PHVW_Commandes += qtéCommandes
                entry.PHVW_Buckets.push(bucket)
                weekTotals.PHVW += realQuantité
                break
              case siteId_JPLG:
                entry.JPLG += realQuantité
                entry.JPLG_Commandes += qtéCommandes
                entry.JPLG_Buckets.push(bucket)
                weekTotals.JPLG += realQuantité
                break
            }
          }
        }

        crunch.days.push(entry)
      }

      crunches.push(crunch)
    }

    newData.value = false
  }
  catch (e) {
    notifyError(e)
  }
  finally {
    isLoading.value = false
  }
}

function getCrunchDay(culture: string, cellDay: Date | string | undefined) {
  const crunch = crunches.find(c => c.culture == culture)
  if (!crunch) {
    return undefined
  }

  const day = crunch.days.find((d: { date: string | Date | undefined }) => d.date == cellDay)
  return day
}

function cellHasTooltip(field: string, culture: string, cellDay: Date | string | undefined) {
  const day = getCrunchDay(culture, cellDay)
  if (!day) {
    return false
  }
  return day[field + '_Buckets'] != undefined && day[field + '_Buckets'].length > 0
}

function getCellTooltip(field: string, culture: string, cellDay: Date | string | undefined) {
  const day = getCrunchDay(culture, cellDay)
  if (!day) {
    return false
  }

  let tooltip = ''

  const buckets = day[field + '_Buckets']
  buckets.sort((a: MDBVegcoBucket, b: MDBVegcoBucket) => {
    if (a.numéroSemi < b.numéroSemi) return -1
    if (a.numéroSemi > b.numéroSemi) return 1
    return 0
  })

  for (const bucket of buckets) {
    if (bucket.semiFermé && bucket.commandeVenteId == emptyMongoId())
      continue

    const qté = roundTo(bucket.quantité / unitésParBoîte(bucket.culture), 0)
    if (qté == 0) continue

    tooltip += `Semi ${bucket.numéroSemi}: ${qté}`

    if (bucket.commandeVenteId != emptyMongoId()) {
      const commande = storeVegco.commandesStore.find(c => c.id == bucket.commandeVenteId)
      if (commande) {
        tooltip += ` -> #${commande.noCommande}`
      } else if (storeVegco.commandeInventaire != undefined && bucket.commandeVenteId == storeVegco.commandeInventaire.id) {
        tooltip += ` -> Pour Inv`
      } else {
        tooltip += ` -> ?`
      }

      if (bucket.codeProduit) {
        tooltip += `: ${bucket.codeProduit}`
      }
    }

    tooltip += '\n'
  }

  return tooltip
}

function getCellValue(field: string, culture: string, cellDay: Date | string | undefined, idx: number) {
  const crunch = crunches.find(c => c.culture == culture)
  if (!crunch) {
    return ''
  }

  if (cellDay == 'Total') {
    const weekNumber = (idx + 2) / 9 - 1
    const weekTotal = crunch.weekTotals[weekNumber]
    if (!weekTotal) {
      return ''
    }

    return weekTotal[field] == undefined ? '' : roundTo(weekTotal[field], 0)
  }

  const day = crunch.days.find((d: { date: string | Date | undefined }) => d.date == cellDay)
  if (!day) {
    return ''
  }

  if (field == 'total') {
    return day[field] == undefined ? '' : roundTo(day[field], 0)
  }

  /*if (field == 'commandesInv' || field == 'commandesNon' || field == 'inventaires') {
    return (day[field] == undefined || day[field] == 0) ? '' : `(${roundTo(day[field], 0)})`
  }*/

  let commandesString = ''
  if (day[field + '_Commandes'] != undefined && day[field + '_Commandes'] > 0) {
    commandesString = '*'
  }

  return (day[field] == undefined ? '' : day[field] == 0 ? '' : roundTo(day[field], 0)) + commandesString
}

function getTableRows(culture: string) {
  if (culture == 'Laitue Feuille Verte' || culture == 'Laitue Feuille Rouge') {
    return tableRowsJPL
  } else if (culture == 'Bok Choy' || culture == 'Nappa' || culture == 'Fenouil') {
    return tableRowsHotte
  }
  return tableRows
}

function getColumnSize(idx: number) {
  if (idx % 9 == 8) {
    return '75px'
  }
  return '100px'
}

const theme = useTheme()
function getColumnColor(idx: number) {
  const themeName = theme.global.name.value
  if (idx % 9 == 8) {
    return ''
  }
  if (idx % 9 == 7) {
    return themeName == 'dark' ? '#101040' : '#A0A0FF'
  }
  if ((idx / 8) % 2 < 1) {
    return themeName == 'dark' ? '#104010' : '#A0FFA0'
  }
  else {
    return themeName == 'dark' ? '#505010' : '#FFFFA0'
  }
}

function getCellClass(row: any, day: any) {
  let classes = ''

  if (day == undefined || day == 'Total') {
    classes += 'gap-between-weeks '
  }

  if (day == 'Total') {
    if (row && (row.field == 'total' || row.field == 'totalJournée')) {
      classes += 'total-column-plus '
    } else {
      classes += 'total-column '
    }
  }

  if (row?.field == 'total') {
    classes += 'big-borders-bottom '
  } else if (row?.field == 'totalJournée') {
    classes += 'small-borders-bottom '
  }

  if (row && row.thClass) {
    classes += row.thClass
  }

  return classes == '' ? 'small-borders' : classes
}

function print() {
  const printWindow = window.open('', '_blank')
  if (printWindow) {
    printWindow.document.open()
    printWindow.document.write(generateReportHTML())
    printWindow.document.close()
    //setTimeout(() => printWindow.print(), 500)
  }
}

function generateReportHTML() {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Rapport des producteurs</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ddd; border-left: 1px solid #000; border-right: 1px solid #000; padding: 2px; text-align: center; }
        .culture-heading { background-color: #f2f2f2; font-size: 14px; text-align: left; padding: 6px; font-weight: bold; }
        .gap-between-weeks { border-left: 3px solid #000; border-right: 3px solid #000; }
        .total-column { background-color: #f2f2f2; }
        .total-column-plus { background-color: #e0e0e0; }
        .small-borders-bottom { border-bottom: 2px solid #000; border-top: 2px solid #000; font-weight: bold; }
        .big-borders-bottom { border-bottom: 5px solid #000; border-top: 5px solid #000; font-weight: bold; }
      </style>
    </head>
    <body>
      <h3>Rapport des producteurs - Généré le: ${to_wd_dd_MMMM_hh_mm(new Date())}</h3>
      <table>
        <thead>
          <tr>
            <th></th>
            ${days.value.map((day, idx) =>
              day == undefined ? '<th class="gap-between-weeks"></th>' :
              (typeof day === 'string' ? `<th class="${idx % 9 == 8 ? 'total-column' : ''}">${day}</th>` :
              `<th>${to_wd(day)}</th>`)
            ).join('')}
          </tr>
          <tr>
            <th></th>
            ${days.value.map((day, idx) =>
              day == undefined ? '<th class="gap-between-weeks"></th>' :
              (typeof day === 'string' ? `<th class="${idx % 9 == 8 ? 'total-column' : ''}">${day}</th>` :
              `<th>${to_dd_MM(day)}</th>`)
            ).join('')}
          </tr>
        </thead>

        ${cultures.value.map(culture => `
          <tbody>
            <tr>
              <th colspan="${days.value.length + 1}" class="culture-heading">${culture}</th>
            </tr>
            ${getTableRows(culture).map(row => `
              <tr>
                <th class="${row.thClass || ''}">${row.label}</th>
                ${days.value.map((day, idx) => {
                  const cellValue = getCellValue(row.field, culture, day, idx);
                  //const isTotal = day === 'Total';
                  const rowClass = [];

                  if (day === undefined || day === 'Total') {
                    rowClass.push('gap-between-weeks');
                  }

                  if (day === 'Total') {
                    if (row.field === 'total' || row.field === 'totalJournée') {
                      rowClass.push('total-column-plus');
                    } else {
                      rowClass.push('total-column');
                    }
                  }

                  if (row.field === 'total') {
                    rowClass.push('big-borders-bottom');
                  } else if (row.field === 'totalJournée') {
                    rowClass.push('small-borders-bottom');
                  }

                  const classList = rowClass.length > 0 ? `class="${rowClass.join(' ')}"` : '';

                  return `<td ${classList}>${cellValue}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        `).join('')}
      </table>
    </body>
    </html>
  `;
}

function changeSelectionSum() {
  selectedSum.value = computeSelectionSum()
}

const intervalId = setInterval(async () => {
  if (await storeVegco.isRevisionChanged())
    newData.value = true
}, 3000)

onMounted(() => {
  getData()
  document.addEventListener('selectionchange', changeSelectionSum)
})

onUnmounted(() => {
  if (intervalId)
    clearInterval(intervalId)
  document.removeEventListener('selectionchange', changeSelectionSum)
})
</script>

<template>
  <div v-if="selectedSum !== null" style="position: fixed; bottom: 36px; right: 32px; background: #222; color: #fff; padding: 12px 24px; border-radius: 8px; z-index: 9999;">
    Somme de la sélection : {{ selectedSum }}
  </div>
  <v-card class="mx-4 my-4 page-card" rounded="xl" style="height: 85vh; display: flex; flex-direction: column;" :loading="isLoading">

    <!-- Page header -->
    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">

        <!-- Left: title -->
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="mdi-account-group" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">Rapport des producteurs</div>
            <div class="text-caption text-medium-emphasis">Données hebdomadaires par producteur</div>
          </div>
        </div>

        <!-- Right: actions -->
        <div class="d-flex align-center" style="gap: 8px;">
          <span class="text-body-2 text-medium-emphasis">* indique que des quantités proviennent de buckets attribuées</span>
          <v-btn v-if="newData" color="primary" variant="text" prepend-icon="mdi-refresh" @click="getData()">Recharger les données</v-btn>
          <v-btn color="primary" variant="tonal" rounded="pill" icon="mdi-printer" @click="print()"/>
        </div>
      </div>

      <div class="d-flex align-center flex-wrap mt-3" style="gap: 8px;">
        <WeekSelector ref="weekSelector" v-model="selectedWeek" v-model:number-of-days="numberOfDays"/>
        <v-spacer/>
        <v-text-field
          v-model="numberOfWeeks" label="Nombre de semaines"
          type="number" min="1" max="4"
          density="compact" variant="outlined" rounded="lg" hide-details
          style="max-width: 200px;"
          @change="onWeekNumberChanged"/>
      </div>
    </div>
    <v-divider/>

    <div style="flex: 1 1 auto; overflow-x: auto; overflow-y: auto;" class="bigger-scrollbar">
      <table class="big-table">
        <thead>
          <tr>
            <th :style="`min-width:${$vuetify.display.xs ? 100 : 250}px`"/>
            <th
              v-for="(day, idx) in days" :key="idx"
              :style="`min-width: ${getColumnSize(idx)}`">
              {{ day == undefined ? '' : typeof day === 'string' ? day : to_wd(day) }}
            </th>
          </tr>
          <tr>
            <th/>
            <th
              v-for="(day, idx) in days" :key="idx"
              :style="`background-color: ${getColumnColor(idx)};`">
              {{ day == undefined ? '' : typeof day === 'string' ? day : to_dd_MM(day) }}
            </th>
          </tr>
        </thead>
        <tbody v-for="(culture, idcx) in cultures" :key="idcx">
          <tr>
            <th :class="`${$vuetify.display.xs ? 'text-subtitle1' : 'text-h5'} pt-3 culture-heading`">{{ culture }}</th>
            <th
              v-for="(day, idx) in days" :key="idx"
              style="overflow-x: scroll; white-space: nowrap;"
              :class="getCellClass(undefined, day)"/>
          </tr>
          <tr v-for="(row, rowIdx) in getTableRows(culture)" :key="rowIdx">
            <th :class="row.thClass || 'small-borders'">{{ row.label }}</th>
            <th
              v-for="(day, idx) in days" :key="idx"
              style="overflow-x: scroll; white-space: nowrap;"
              :class="getCellClass(row, day)">
              <span>{{ getCellValue(row.field, culture, day, idx) }}</span>
              <v-tooltip v-if="cellHasTooltip(row.field, culture, day)" activator="parent" location="top">
                <pre>{{ getCellTooltip(row.field, culture, day) }}</pre>
              </v-tooltip>
            </th>
          </tr>
        </tbody>
      </table>
    </div>
  </v-card>
</template>

<style scoped>
/* ── Page header ─────────────────────────── */
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}

  .culture-heading {
    height: 75px;
    min-height: 75px;
    padding-left: 16px;
    padding-right: 16px;
    border-right: 1px solid;
  }

  .gap-between-weeks {
    border-left: 3px solid;
    border-right: 3px solid;
    scrollbar-width: none;
  }

  .small-borders {
    padding-left: 16px;
    padding-right: 16px;
    font-weight: normal;
    border-right: 1px solid;
    border-top: 0px;
    border-bottom: 0px;
    scrollbar-width: none;
  }

  th.gap-between-weeks.small-borders-bottom {
    border-right: 3px solid;
    scrollbar-width: none;
  }

  th.gap-between-weeks.big-borders-bottom {
    border-right: 3px solid;
    scrollbar-width: none;
  }

  .small-borders-bottom {
    border-right: 1px solid;
    border-top: 1px solid;
    border-bottom: 1px solid;
    padding-left: 16px;
    padding-right: 16px;
    font-weight: bold;
    scrollbar-width: none;
  }

  .big-borders-bottom {
    border-right: 1px solid;
    border-top: 1px solid;
    border-bottom: 5px solid;
    padding-left: 16px;
    padding-right: 16px;
    font-weight: bold;
    scrollbar-width: none;
  }

  /* For Firefox */
  div[style*="overflow-x: auto"] {
    scrollbar-width: thick;
  }
  /* For Chrome, Edge, Safari */
  div[style*="overflow-x: auto"]::-webkit-scrollbar {
    height: 12px;
  }
  div[style*="overflow-x: auto"]::-webkit-scrollbar-thumb {
    background: #bdbdbd;
    border-radius: 8px;
  }

  .big-table {
    border-collapse: collapse;
    table-layout: fixed; /* Ensures that the table respects the width of its columns */
  }

  .total-column {
    background-color: v-bind('theme.global.name.value === "dark" ? "#323232" : "#e5e5e5"');
  }

  .total-column-plus {
    background-color: v-bind('theme.global.name.value === "dark" ? "#484848" : "#d5d5d5"');
  }

  /* Alternate row background color */
  tr:nth-of-type(even) {
    background-color: v-bind('theme.global.name.value === "dark" ? "#333" : "#f5f5f5"');
  }

  /* Fix the first column */
  .big-table > td:first-child, th:first-child {
    position: sticky;
    left: 0;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"'); /* Set background based on theme */
    z-index: 4; /* Ensure it stays above other content */
  }

  /* Make the first row sticky */
  .big-table thead tr:first-child th {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"');
  }

  /* Make the second row sticky, just below the first */
  .big-table thead tr:nth-child(2) th {
    position: sticky;
    top: 24px;
    z-index: 2;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"');
  }

  /* Make the first cell sticky */
  .big-table thead tr:first-child th:first-child{
    position: sticky;
    left: 0px;
    top: 0px;
    z-index: 3;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"');
  }

  /* Make the second cell sticky, just below the first */
  .big-table thead tr:nth-child(2) th:first-child {
    position: sticky;
    left: 0px;
    top: 24px;
    z-index: 3;
    background-color: v-bind('theme.global.name.value === "dark" ? "#222" : "#fff"');
  }

  .bigger-scrollbar {
    scrollbar-width: auto;
  }

</style>
